import { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { stringToRGB } from "../../utils/colorCode";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

interface SumOfCategory {
  type: "expense" | "income";
  category: string;
  amount: number;
}

const ReportLeft = () => {
  const transactions = useSelector(
    (state: RootState) => state.app.transactions,
  );
  const [type, setType] = useState<"expense" | "income">("expense");

  const sumEachCategory: SumOfCategory[] = [];
  transactions
    .filter((item) => item.type === type)
    .forEach((tr) => {
      const index = sumEachCategory.findIndex(
        (sc) => sc.type === tr.type && sc.category === tr.category,
      );
      if (index > -1) {
        sumEachCategory[index].amount += tr.amount;
      } else {
        sumEachCategory.push({
          type: tr.type,
          category: tr.category,
          amount: tr.amount,
        });
      }
    });

  const labels = sumEachCategory.map(
    (item) => item.type === type && item.category,
  );
  const labelColors = sumEachCategory.map((item) =>
    stringToRGB(item.category, 0.1),
  );
  const labelBorderColors = sumEachCategory.map((item) =>
    stringToRGB(item.category, 0.5),
  );
  const dataSetData = sumEachCategory.map(
    (item) => item.type === type && item.amount,
  );

  const data = {
    labels,
    datasets: [
      {
        label: type === "expense" ? "支出" : "収入",
        data: dataSetData,
        backgroundColor: labelColors,
        borderColor: labelBorderColors,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
  };
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("errors");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#fff",
        p: 2,
      }}
    >
      <FormControl fullWidth>
        <InputLabel id="type-label">
          {tCommon("labels.incomeExpenseType")}
        </InputLabel>
        <Select
          className="MuiSelect-filled"
          labelId="type-label"
          id="type-select"
          label={tCommon("labels.incomeExpense")}
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="expense">{tCommon("transaction.income")}</MenuItem>
          <MenuItem value="income">{tCommon("transaction.expense")}</MenuItem>
        </Select>
      </FormControl>

      <Stack
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            height: "100%",
            minHeight: 0,
            position: "relative",
          }}
        >
          <Pie data={data} options={options} />
        </Box>
      </Stack>
    </Box>
  );
};

export default ReportLeft;
